import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { CustomerAddressDataGridApi } from "src/api/generated/erp/sales/customer/api/customerAddressDataGridApi.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import {
	coloredBooleanColumn,
	enumColumn,
	integerColumn,
	textColumn
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getCustomerAddressTypeLabels } from "src/api/generated/erp/db/types/enums/customerAddressType.ts";
import { CustomerAddressView } from "src/api/generated/erp/db/types/tables/customerAddressView.ts";
import { faCheck, faCopy } from "@fortawesome/pro-regular-svg-icons";
import { CustomerAddressForm } from "src/components/views/erp/sales/customer/CustomerAddressForm.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { CustomerAddressEditApi } from "src/api/generated/erp/sales/customer/api/customerAddressEditApi.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { getCountryLabels } from "src/components/common/country/countries.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerAddressDataGridProps {
	customerId: number;
}

export const CustomerAddressDataGrid = ({ customerId }: CustomerAddressDataGridProps) => {
	const { openDialog } = useGenericDialog();
	const hasPermission = useHasPermission();
	const editable = hasPermission(Permissions.Sales.Customers.MANAGE);
	return (
		<ClientSideDataGridModel
			customFunctionUiActionView={"CUSTOMER_ADDRESSES"}
			gridId={"418ADEBE6EC3F4D2"}
			fetchData={async () => await CustomerAddressDataGridApi.getCustomerAddresses({ customerId: customerId })}
			getRows={(data) => data}
			getRowId={(row) => row.customerAddressId}
			initialParams={{}}
			render={({ dataGridProps, refreshData, onlySelectedRow }) => (
				<FormCrudDataGrid<CustomerAddressView>
					disableMultipleRowSelection
					columns={[
						textColumn({
							field: "name",
							headerName: i18n.t("description"),
							width: 200,
						}),
						textColumn({
							field: "recipientName",
							headerName: i18n.t("recipient"),
							width: 200,
						}),
						textColumn({
							field: "address_1",
							headerName: i18n.t("address_1"),
							width: 200,
						}),
						textColumn({
							field: "address_2",
							headerName: i18n.t("address_2"),
						}),
						textColumn({
							field: "postalCode",
							headerName: i18n.t("postal_code"),
						}),
						textColumn({
							field: "city",
							headerName: i18n.t("city"),
						}),
						enumColumn({
							field: "country",
							headerName: i18n.t("country"),
							enumLabels: getCountryLabels(),
						}),
						textColumn({
							field: "contact",
							headerName: i18n.t("contact"),
							width: 200,
						}),
						textColumn({
							field: "phone",
							headerName: i18n.t("phone"),
							width: 200,
						}),
						textColumn({
							field: "email",
							headerName: i18n.t("email"),
							width: 200,
						}),
						enumColumn({
							field: "addressType",
							headerName: i18n.t("type"),
							enumLabels: getCustomerAddressTypeLabels(),
						}),
						textColumn({
							field: "transportRouteName",
							headerName: i18n.t("transport_route"),
							width: 200,
						}),
						coloredBooleanColumn({
							field: "isDefault",
							headerName: i18n.t("default"),
							falseColor: "grey",
						}),
						integerColumn({
							field: "customerAddressId",
							headerName: i18n.t("id"),
						}),
					]}
					rowContextMenuComponents={
						editable ?
							({ row }) => [
								<AsyncMenuButton
									key={"setDefault"}
									icon={faCheck}
									label={i18n.t("set_default")}
									onClick={async () => {
										await CustomerAddressEditApi.setCustomerAddressDefault({
											customerAddressId: row.customerAddressId,
										});
										await refreshData();
									}}
								/>,
								<AsyncMenuButton
									key={"copy"}
									icon={faCopy}
									label={i18n.t("copy")}
									onClick={async () => {
										openFormOnDialog({
											onSubmit: refreshData,
											title: i18n.t("new_address"),
											size: "sm",
											openDialog,
											component: CustomerAddressForm,
											agentContext: {
												key: defineAgentUiContextKey("erp.sales.customerAddress.copyFormDialog"),
												data: {
													customerId,
													copyCustomerAddressSourceId: row.customerAddressId,
												},
											},
											props: {
												customerId: customerId,
												customerAddressId: undefined,
												copyCustomerAddressSource: row,
											},
										});
									}}
								/>,
							]
						:	undefined
					}
					form={{
						addRowEnabled: editable,
						editEnabled: editable,
						dialogSize: "sm",
						dialogTitle: i18n.t("customer_address"),
						agentContext: (row) => ({
							key: defineAgentUiContextKey("erp.sales.customerAddress.formDialog"),
							pathSegments:
								row == null ? undefined : [`${i18n.t("customer_address")} ${row.customerAddressId}`],
							data: {
								customerId,
								customerAddressId: row?.customerAddressId ?? null,
							},
						}),
						component: ({ row, ...other }) => (
							<CustomerAddressForm
								customerId={customerId}
								customerAddressId={row?.customerAddressId}
								{...other}
							/>
						),
					}}
					remove={
						!editable ? undefined
						: onlySelectedRow?.isDefault === true ?
							{
								type: "disabled",
								tooltip: i18n.t("default_address_cannot_be_deleted"),
							}
						:	({ row }) =>
								CustomerAddressEditApi.delete_({
									customerAddressId: row.customerAddressId,
								})
					}
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.customerAddresses.grid"),
						data: {
							customerId,
						},
						selectedRowPathSegment: (row) => `${i18n.t("customer_address")} ${row.customerAddressId}`,
						rowFields: ["customerAddressId", "customerId", "addressType", "name"],
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
